import { BinIcon, PlusIcon } from '@systemeio/ui/icons'
import { Hint, NeutralButton, NeutralTypeEnum, DateTimePicker } from '@systemeio/ui'
import { greenwichTimeZone } from 'modules/calendar/constants'
import ButtonLikeLink from 'shared/components/button-like-link'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'
import useUser from 'shared/hooks/use-user'
import { twMerge } from 'tailwind-merge'
import { useDraftUnavailabilitySlots } from '../hooks/use-draft-availability-slots'
import { AvailabilitySlotsSkeleton } from './availability-slots-skeleton'

interface DateUnavailabilitySlotsProps
  extends Omit<ReturnType<typeof useDraftUnavailabilitySlots>, 'reinitiate'> {
  isLoading?: boolean
  disabled?: boolean
  titleClassName?: string
}

export function DateUnavailabilitySlots({
  slots,
  addSlot,
  updateSlot,
  removeSlot,
  isLoading,
  disabled,
  titleClassName,
}: DateUnavailabilitySlotsProps) {
  const { t } = useLocoTranslation()
  const { user, intlLocale } = useUser()

  return (
    <>
      <div
        className={twMerge(
          'flex w-full flex-col items-start gap-2 md:flex-row md:items-center md:justify-between',
          slots.length > 0 && 'mb-5',
          titleClassName,
        )}
      >
        <Hint label={t('dashboard.calendar.time_off_tooltip')}>
          <p>{t('dashboard.calendar.time_off')}</p>
        </Hint>

        <ButtonLikeLink
          type="button"
          onClick={() => {
            addSlot({
              startDate: '',
              endDate: '',
            })
          }}
          className={'flex items-center gap-1 text-sm'}
          disabled={disabled}
        >
          <PlusIcon />
          {t('dashboard.calendar.add_date_slot')}
        </ButtonLikeLink>
      </div>
      <div>
        {isLoading && <AvailabilitySlotsSkeleton />}
        {!isLoading &&
          slots?.map((slot, index) => {
            const isTimeBeforeEnd = (time: Date) => {
              const selectedTime = new Date(time).getTime()
              const currentTime = new Date().getTime()
              const endTime = slot.endDate ? new Date(slot.endDate).getTime() : Infinity

              return selectedTime > currentTime && selectedTime < endTime
            }

            const isTimeAfterStart = (time: Date) => {
              const selectedTime = new Date(time).getTime()
              const startTime = new Date(slot.startDate || new Date()).getTime()

              return selectedTime > startTime
            }

            return (
              <div
                key={index}
                className="border-text-secondary/30 flex flex-row flex-wrap items-end justify-between gap-2 gap-y-3 border-t py-5 last:border-b"
              >
                <div className="flex flex-wrap items-center gap-2">
                  <DateTimePicker
                    label={t('global.date_from')}
                    placeholder={t('global.select_date_and_time')}
                    value={slot.startDate}
                    onChange={startDate => {
                      updateSlot(
                        {
                          ...slot,
                          ...(startDate ? { startDate } : {}),
                        },
                        index,
                      )
                    }}
                    customProps={{
                      minDate: new Date(),
                      maxDate: new Date(slot.endDate),
                      filterTime: isTimeBeforeEnd,
                    }}
                    disabled={disabled}
                    timezone={greenwichTimeZone}
                    createdAt={user?.createdAt}
                    intlLocale={intlLocale}
                    disableApplyButton={date => !isTimeBeforeEnd(date)}
                  />

                  <span className="bg-background-tertiary mt-6 hidden h-[2px] w-3 rounded-full sm:block" />

                  <DateTimePicker
                    label={t('global.date_to')}
                    placeholder={t('global.select_date_and_time')}
                    value={slot.endDate}
                    onChange={endDate => {
                      updateSlot(
                        {
                          ...slot,
                          ...(endDate ? { endDate } : {}),
                        },
                        index,
                      )
                    }}
                    customProps={{
                      minDate: new Date(slot.startDate || new Date()),
                      filterTime: isTimeAfterStart,
                    }}
                    disabled={disabled}
                    timezone={greenwichTimeZone}
                    createdAt={user?.createdAt}
                    intlLocale={intlLocale}
                    disableApplyButton={date => !isTimeAfterStart(date)}
                  />
                </div>

                <NeutralButton
                  neutralType={NeutralTypeEnum.danger}
                  type="button"
                  onClick={() => removeSlot(index)}
                  disabled={disabled}
                >
                  <BinIcon
                    width={16}
                    height={16}
                    className="fill-text-primary group-hover:fill-danger transition-all"
                  />
                  <span className="hidden sm:block">{t('global.remove')}</span>
                </NeutralButton>
              </div>
            )
          })}
      </div>
    </>
  )
}
